import type { ComponentType } from 'react'
import { memo, useCallback, useRef } from 'react'

import { useUnit } from 'effector-react'
import type {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
} from 'react-query'
import type { ListChildComponentProps, ListOnScrollProps } from 'react-window'
import { VariableSizeList } from 'react-window'

import ArrowIcon from '@/assets/icons/icon-arrow.svg'
import { $creatorId } from '@/models/creators.ts'
import { useResizeObserver } from '@/utils/useResizeObserver.ts'

import type { SocialAccountVideosResponse, SocialVideo } from '../../types'
import { ListScrollDirection } from '../../types'
import { CarouselItem } from './CarouselItem'

interface VideoCarouselProps {
  items: SocialVideo[]
  itemsLoading: boolean
  onLoadMore: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<
    InfiniteQueryObserverResult<SocialAccountVideosResponse, unknown>
  >
}

/**
 * This component is responsible for rendering a carousel of TikTokVideo items.
 * It uses the react-window library to efficiently render a large number of items. https://github.com/bvaughn/react-window
 */
const VideoCarouselComponent = ({
  items,
  onLoadMore,
  itemsLoading,
}: VideoCarouselProps) => {
  const creatorId = useUnit($creatorId)
  const listElementRef = useRef<HTMLDivElement>(null)
  const lastVisibleIndexRef = useRef<number | null>(null)
  const [parentRef, width] = useResizeObserver()

  const scrollOffsetInitialRef = useRef<number>(0)

  const renderListItemFn: ComponentType<
    ListChildComponentProps<SocialVideo[]>
  > = useCallback(
    ({ data, index, style }: ListChildComponentProps<SocialVideo[]>) => {
      const video = data[index]

      // Calculate right margin for each item to create spacing between them.
      const marginRight = 24
      const itemStyle = {
        ...style,
        paddingRight: `${marginRight}px`,
      }

      if (lastVisibleIndexRef.current !== undefined) {
        lastVisibleIndexRef.current = index
      }

      return (
        <CarouselItem
          data={video}
          style={itemStyle}
          creatorId={creatorId!}
        />
      )
    },
    [creatorId],
  )

  const itemSize = useCallback((_index: number) => {
    // Todo: Calculate the width of a video item at the specified index to fit the TikTok aspect ratio.
    return 300
  }, [])

  const onScroll = useCallback(
    ({
      scrollDirection,
      scrollUpdateWasRequested,
      scrollOffset,
    }: ListOnScrollProps) => {
      const lastItemIndex = lastVisibleIndexRef.current

      if (!scrollUpdateWasRequested) {
        scrollOffsetInitialRef.current = scrollOffset
      }

      // Fetch if the last fourth item is visible
      if (
        lastItemIndex === null ||
        lastItemIndex !== items.length - 4 ||
        itemsLoading
      ) {
        return
      }

      if (scrollDirection === ListScrollDirection.forward) {
        onLoadMore()
      }
    },
    [items, itemsLoading, onLoadMore],
  )

  const scrollTo = useCallback(
    (scrollOffset: number) => {
      if (listElementRef.current) {
        const offset = (scrollOffsetInitialRef.current =
          scrollOffsetInitialRef.current + scrollOffset)

        listElementRef.current.scrollTo({
          left: offset,
          behavior: 'smooth',
        })
      }
    },
    [scrollOffsetInitialRef],
  )

  return (
    <div
      className="relative flex gap-2 overflow-x-hidden"
      ref={parentRef}
    >
      <button
        className="absolute right-0 top-1/2 z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-md bg-secondary-500 transition-colors hover:bg-secondary-300 dark:bg-white dark:hover:bg-secondary-100"
        onClick={() => scrollTo(350)}
      >
        <ArrowIcon className="h-8 w-8 text-white dark:text-secondary-500 " />
      </button>
      <button
        className="absolute left-0 top-1/2  z-20 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-md bg-secondary-500 shadow-neutral-default transition-colors hover:bg-secondary-300 dark:bg-white dark:hover:bg-secondary-100"
        onClick={() => scrollTo(-350)}
      >
        <ArrowIcon className="h-8 w-8 rotate-180 text-white dark:text-secondary-500" />
      </button>

      <VariableSizeList
        className="scrollbar-hide"
        layout="horizontal"
        height={500}
        itemSize={itemSize}
        itemCount={items.length}
        width={width}
        itemData={items}
        onScroll={onScroll}
        outerElementType="div"
        outerRef={listElementRef}
      >
        {renderListItemFn}
      </VariableSizeList>
    </div>
  )
}

export const VideoCarousel = memo(VideoCarouselComponent)
